import { create } from 'zustand';
import { messagesAPI } from '../lib/api';
import { hydrateMessageAuthor, MessageAuthor } from '../lib/messageAuthors';
import { applyMessageUpdate, applyReactionUpdate, normalizeMessage } from '../lib/messageEvents';
import { 
  joinChatChannel as joinChannel, 
  joinDirectMessage, 
  sendChannelMessage,
  sendDirectMessage as sendDirectMsg,
  editMessage as socketEditMessage, 
  deleteMessage as socketDeleteMessage,
  addReaction as socketAddReaction,
  removeReaction as socketRemoveReaction,
  getSocket,
} from '../lib/realtime';

interface Message {
  id: string;
  content: string;
  user_id: string;
  channel_id: string;
  created_at: string;
  edited: boolean;
  deleted_at?: string | null;
  attachments: any[] | null;
  reactions: any | null;
  reply_to: string | null;
  author?: MessageAuthor | null;
  user?: {
    username: string;
    avatar_url: string | null;
  };
}

interface DirectMessage {
  id: string;
  content: string;
  sender_id: string;
  recipient_id: string;
  created_at: string;
  edited: boolean;
  deleted_at?: string | null;
  attachments: any[] | null;
  reactions: any | null;
  author?: MessageAuthor | null;
  sender?: {
    username: string;
    avatar_url: string | null;
  };
}

interface UnreadDm {
  sender_id: string;
  unread_count: number;
  last_message_at: string;
  username: string;
  avatar_url: string | null;
  is_system: boolean;
}

interface DmConversation {
  user_id: string;
  username: string;
  avatar_url: string | null;
  is_system: boolean;
  last_message: string;
  last_message_sender_id: string;
  last_message_at: string;
  unread_count: number;
}

interface MessageState {
  messages: Message[];
  directMessages: DirectMessage[];
  currentDmUser: string | null;
  isLoading: boolean;
  error: string | null;
  typingUsers: Record<string, { userId: string; username: string }[]>;
  currentChannelId: string | null;
  unreadDms: UnreadDm[];
  conversations: DmConversation[];
  
  // Expose these APIs directly to components
  messagesAPI: typeof messagesAPI;
  joinChannel: typeof joinChannel;
  joinDirectMessage: typeof joinDirectMessage;
  getSocket: typeof getSocket;
  
  // Original methods
  fetchChannelMessages: (channelId: string) => Promise<Message[]>;
  fetchDirectMessages: (userId: string, otherUserId: string) => Promise<void>;
  sendMessage: (content: string, channelId: string, replyTo?: string | null, serverId?: string) => Promise<void>;
  sendDirectMessage: (content: string, recipientId: string) => Promise<void>;
  editMessage: (messageId: string, content: string, isDm?: boolean, topicId?: string, serverId?: string) => Promise<void>;
  deleteMessage: (messageId: string, isDm?: boolean, topicId?: string, serverId?: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string, isDm?: boolean, topicId?: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string, isDm?: boolean, topicId?: string) => Promise<void>;
  setCurrentDmUser: (userId: string | null) => void;
  showDmVoiceCall: boolean;
  setShowDmVoiceCall: (show: boolean) => void;
  subscribeToMessages: (channelId: string) => () => void;
  subscribeToDirectMessages: (userId: string, otherUserId: string) => () => void;
  setTypingUser: (userId: string, isTyping: boolean) => void;
  fetchUnreadDms: () => Promise<void>;
  fetchConversations: () => Promise<void>;
  updateConversation: (userId: string, content: string, senderId: string) => void;
  incrementUnreadDm: (senderId: string, username: string, avatarUrl: string | null, isSystem?: boolean) => void;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  messages: [],
  directMessages: [],
  currentDmUser: null,
  isLoading: false,
  error: null,
  typingUsers: {},
  currentChannelId: null,
  unreadDms: [],
  conversations: [],
  
  // Expose these APIs directly to components
  messagesAPI,
  joinChannel,
  joinDirectMessage,
  getSocket,
  
  fetchChannelMessages: async (channelId) => {
    try {
      set({ isLoading: true, error: null });
      console.log(`MessageStore: Starting to fetch messages for channel ${channelId}`);
      
      const messages = (await messagesAPI.getChannelMessages(channelId))
        ?.map(normalizeMessage)
        .filter((message: Message) => !message.deleted_at);
      console.log(`MessageStore: Received ${messages?.length || 0} messages`);
      
      // S'assurer que nous sommes toujours sur le même canal avant de mettre à jour
      if (get().currentChannelId === channelId) {
        set({ 
          messages: messages || [], 
          isLoading: false,
          currentChannelId: channelId
        });
      }
      
      return messages || [];
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      set({ 
        error: error.message || 'Failed to fetch messages',
        isLoading: false
      });
      return [];
    }
  },
  
  fetchDirectMessages: async (_userId, otherUserId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Get direct messages from API
      const messages = (await messagesAPI.getDirectMessages(otherUserId))
        ?.map(normalizeMessage)
        .filter((message: DirectMessage) => !message.deleted_at);
      
      set({ directMessages: messages || [], currentDmUser: otherUserId });
    } catch (error: any) {
      console.error('Error fetching direct messages:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch direct messages' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  sendMessage: async (content, channelId, replyTo = null, serverId) => {
    try {
      set({ error: null });
      
      // Send message via Phoenix WebSocket — published to NATS by Phoenix
      sendChannelMessage(content, channelId, replyTo, serverId);
      
      // The message will appear via the Phoenix channel new_message event
    } catch (error: any) {
      console.error('Error sending message:', error);
      set({ error: error.message || 'Failed to send message' });
    }
  },
  
  sendDirectMessage: async (content, recipientId) => {
    try {
      set({ error: null });
      
      // Send DM via Phoenix WebSocket — published to NATS by Phoenix
      sendDirectMsg(content, recipientId);

      // Update conversation list with sent message
      const userId = localStorage.getItem('user_id') || '';
      get().updateConversation(recipientId, content, userId);
      
      // The message will appear via the Phoenix channel new_message event
    } catch (error: any) {
      console.error('Error sending direct message:', error);
      set({ error: error.message || 'Failed to send direct message' });
    }
  },
  
  editMessage: async (messageId, content, isDm = false, topicId, serverId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Edit message via socket
      socketEditMessage(messageId, content, isDm, topicId, serverId);
      
      // Optimistically update message in state
      if (isDm) {
        set(state => ({
          directMessages: state.directMessages.map(msg => 
        msg.id === messageId ? applyMessageUpdate(msg, { content }) : msg
          )
        }));
      } else {
        set(state => ({
          messages: state.messages.map(msg => 
        msg.id === messageId ? applyMessageUpdate(msg, { content }) : msg
          )
        }));
      }
    } catch (error: any) {
      console.error('Error editing message:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to edit message' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  deleteMessage: async (messageId, isDm = false, topicId, serverId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Delete message via socket
      socketDeleteMessage(messageId, isDm, topicId, serverId);
      
      // Optimistically remove message from state
      if (isDm) {
        set(state => ({
          directMessages: state.directMessages.filter(msg => msg.id !== messageId)
        }));
      } else {
        set(state => ({
          messages: state.messages.filter(msg => msg.id !== messageId)
        }));
      }
    } catch (error: any) {
      console.error('Error deleting message:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to delete message' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  addReaction: async (messageId, emoji, isDm = false, topicId) => {
    socketAddReaction(messageId, emoji, isDm, topicId);
  },
  
  removeReaction: async (messageId, emoji, isDm = false, topicId) => {
    socketRemoveReaction(messageId, emoji, isDm, topicId);
  },
  
  showDmVoiceCall: false,
  setShowDmVoiceCall: (show) => set({ showDmVoiceCall: show }),

  setCurrentDmUser: (userId) => {
    if (!userId) {
      set({
        currentDmUser: null,
        directMessages: [],
        isLoading: false,
      });
      return;
    }

    // Clear unread count for this user and mark as read
    set(state => ({
      isLoading: true,
      currentDmUser: userId,
      unreadDms: state.unreadDms.filter(d => d.sender_id !== userId),
      conversations: state.conversations.map(c =>
        c.user_id === userId ? { ...c, unread_count: 0 } : c
      ),
    }));
    messagesAPI.markDmAsRead(userId).catch(() => {});
    
    // Charger les messages
    const currentUser = localStorage.getItem('user_id') || '';
    get().fetchDirectMessages(currentUser, userId)
      .finally(() => {
        set({ isLoading: false });
      });
  },
  
  subscribeToMessages: (channelId) => {
    // Join channel Phoenix topic
    const channel = joinChannel(channelId);
    if (!channel) return () => {};
    
    // Set up event listeners on the Phoenix channel
    const newMsgRef = channel.on('new_message', async (data: any) => {
      const message = normalizeMessage(await hydrateMessageAuthor(data));
      set(state => ({
        messages: state.messages.some(msg => msg.id === message.id) ? state.messages : [...state.messages, message]
      }));
    });
    
    const updateRef = channel.on('message_updated', (data: any) => {
      set(state => ({
        messages: state.messages.map(msg => 
          msg.id === data.id ? applyMessageUpdate(msg, data) : msg
        )
      }));
    });
    
    const deleteRef = channel.on('message_deleted', (data: any) => {
      set(state => ({
        messages: state.messages.filter(msg => msg.id !== data.id)
      }));
    });
    
    const reactionRef = channel.on('reaction_updated', (data: any) => {
      set(state => ({
        messages: state.messages.map(msg => 
          msg.id === data.messageId ? applyReactionUpdate(msg, data) : msg
        )
      }));
    });
    
    const typingStartRef = channel.on('typing_start', (data: any) => {
      if (!data.roomKey) return;
      set(state => {
        const current = state.typingUsers[data.roomKey] || [];
        if (current.some(t => t.userId === data.userId)) return state;
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: [...current, { userId: data.userId, username: data.username }] } };
      });
    });
    
    const typingStopRef = channel.on('typing_stop', (data: any) => {
      if (!data.roomKey) return;
      set(state => {
        const current = state.typingUsers[data.roomKey] || [];
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: current.filter(t => t.userId !== data.userId) } };
      });
    });
    
    // Return cleanup function
    return () => {
      channel.off('new_message', newMsgRef);
      channel.off('message_updated', updateRef);
      channel.off('message_deleted', deleteRef);
      channel.off('reaction_updated', reactionRef);
      channel.off('typing_start', typingStartRef);
      channel.off('typing_stop', typingStopRef);
    };
  },
  
  subscribeToDirectMessages: (_userId, otherUserId) => {
    // Join DM Phoenix channel
    const channel = joinDirectMessage(otherUserId);
    if (!channel) return () => {};
    
    // Set up event listeners on the Phoenix DM channel
    const newMsgRef = channel.on('new_message', async (data: any) => {
      const message = normalizeMessage(await hydrateMessageAuthor(data));
      set(state => ({
        directMessages: state.directMessages.some(msg => msg.id === message.id) ? state.directMessages : [...state.directMessages, message]
      }));
    });
    
    const updateRef = channel.on('message_updated', (data: any) => {
      set(state => ({
        directMessages: state.directMessages.map(msg => 
          msg.id === data.id ? applyMessageUpdate(msg, data) : msg
        )
      }));
    });
    
    const deleteRef = channel.on('message_deleted', (data: any) => {
      set(state => ({
        directMessages: state.directMessages.filter(msg => msg.id !== data.id)
      }));
    });
    
    const reactionRef = channel.on('reaction_updated', (data: any) => {
      set(state => ({
        directMessages: state.directMessages.map(msg => 
          msg.id === data.messageId ? applyReactionUpdate(msg, data) : msg
        )
      }));
    });
    
    const typingStartRef = channel.on('typing_start', (data: any) => {
      if (!data.roomKey) return;
      set(state => {
        const current = state.typingUsers[data.roomKey] || [];
        if (current.some(t => t.userId === data.userId)) return state;
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: [...current, { userId: data.userId, username: data.username }] } };
      });
    });
    
    const typingStopRef = channel.on('typing_stop', (data: any) => {
      if (!data.roomKey) return;
      set(state => {
        const current = state.typingUsers[data.roomKey] || [];
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: current.filter(t => t.userId !== data.userId) } };
      });
    });
    
    // Return cleanup function
    return () => {
      channel.off('new_message', newMsgRef);
      channel.off('message_updated', updateRef);
      channel.off('message_deleted', deleteRef);
      channel.off('reaction_updated', reactionRef);
      channel.off('typing_start', typingStartRef);
      channel.off('typing_stop', typingStopRef);
    };
  },
  
  setTypingUser: (_userId, _isTyping) => {
    // Legacy — no longer used, kept for interface compat
  },

  fetchUnreadDms: async () => {
    try {
      const data = await messagesAPI.getUnreadDms();
      set({ unreadDms: data || [] });
    } catch {
      // silently ignore
    }
  },

  fetchConversations: async () => {
    try {
      const data = await messagesAPI.getConversations();
      set({ conversations: data || [] });
    } catch {
      // silently ignore
    }
  },

  updateConversation: (userId, content, senderId) => {
    set(state => {
      const existing = state.conversations.find(c => c.user_id === userId);
      const now = new Date().toISOString();
      if (existing) {
        const updated = state.conversations.map(c =>
          c.user_id === userId
            ? { ...c, last_message: content, last_message_sender_id: senderId, last_message_at: now }
            : c
        );
        updated.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());
        return { conversations: updated };
      }
      // If conversation doesn't exist yet, refetch to get the full data
      get().fetchConversations();
      return {};
    });
  },

  incrementUnreadDm: (senderId, username, avatarUrl, isSystem = false) => {
    // Don't increment if this DM is currently open
    if (get().currentDmUser === senderId) return;

    set(state => {
      const existing = state.unreadDms.find(d => d.sender_id === senderId);
      const newUnreadDms = existing
        ? state.unreadDms
            .map(d => d.sender_id === senderId
              ? { ...d, unread_count: d.unread_count + 1, last_message_at: new Date().toISOString() }
              : d)
            .sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime())
        : [
            { sender_id: senderId, unread_count: 1, last_message_at: new Date().toISOString(), username, avatar_url: avatarUrl, is_system: isSystem },
            ...state.unreadDms,
          ];

      // Also increment unread in conversations
      const newConversations = state.conversations.map(c =>
        c.user_id === senderId ? { ...c, unread_count: c.unread_count + 1 } : c
      );

      return { unreadDms: newUnreadDms, conversations: newConversations };
    });
  },
}));
